// Calendar utilities — date math, hour formatting, overlap layout,
// and a deterministic color hash for events that lack a calendarId
// (Phase 1 personal events have no color on the wire, so we derive
// one from the event id so it's stable across reloads).

import type { Calendar, CalendarEvent } from "./rpc";

export const CAL_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const CAL_DAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
export const PALETTE = ["violet", "cyan", "teal", "lime", "amber", "coral", "magenta"];

/** Stable color key from a string id. */
export function colorFromId(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

/** Resolve color for an event: calendar's color (if any) wins, otherwise
 *  a deterministic hash of the event id keeps personal events colored
 *  consistently across reloads. */
export function colorFor(evt: CalendarEvent, calendars?: readonly Calendar[]): string {
  if (evt.calendarId && calendars) {
    const cal = calendars.find((c) => c.id === evt.calendarId);
    if (cal?.color && PALETTE.includes(cal.color)) return cal.color;
    // Fallback to calendarId-derived color so all events on a shared
    // calendar match each other even when no explicit color was set.
    if (cal) return colorFromId(cal.id);
  }
  return colorFromId(evt.id);
}

/** "9 AM", "2:30 PM" — minutes only shown when non-zero. */
export function fmtTime(decimalHour: number): string {
  const h = Math.floor(decimalHour);
  const m = Math.round((decimalHour - h) * 60);
  const period = h >= 12 ? "PM" : "AM";
  const display = ((h + 11) % 12) + 1;
  return m === 0 ? `${display} ${period}` : `${display}:${m.toString().padStart(2, "0")} ${period}`;
}

/** Convert ISO timestamp → decimal hour (0..24) local time. */
export function isoToHour(iso: string): number {
  const d = new Date(iso);
  return d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
}

/** Day-of-week of an ISO timestamp in local time. 0 = Sun. */
export function isoToDow(iso: string): number {
  return new Date(iso).getDay();
}

/** YYYY-MM-DD in local time for an ISO timestamp or all-day date. */
export function localDateKey(iso: string): string {
  // All-day events arrive as "YYYY-MM-DD" already (date-only). Pass
  // through unchanged so we don't shift across midnight via timezone
  // conversion.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Sunday-anchored start of the week containing `date`, local time. */
export function weekStart(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

/** `count` consecutive days starting at `start` (inclusive). */
export function daysFrom(start: Date, count: number): Date[] {
  const out: Date[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push(d);
  }
  return out;
}

/** ISO timestamp at local midnight. Used for range queries. */
export function toIsoLocal(date: Date): string {
  return new Date(date).toISOString();
}

/** "2026-05-21" key from a Date (local). */
export function dateKey(date: Date): string {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** All-day events serialize as "YYYY-MM-DD" — wider than the timed
 *  events (full ISO). Detect via length / no time component. */
export function isDateOnly(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/** Build the 6×7 month grid cells (Sunday-first) for a given month. */
export interface MonthCell {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
}
export function monthCells(year: number, month: number, today: Date): MonthCell[] {
  const first = new Date(year, month, 1);
  const firstDow = first.getDay();
  const totalCells = 42;
  const out: MonthCell[] = [];
  const start = new Date(first);
  start.setDate(first.getDate() - firstDow);
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push({
      date: d,
      inMonth: d.getMonth() === month,
      isToday: sameDay(d, today),
    });
  }
  return out;
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** ISO at hour-of-day (decimal allowed) on a given date, local time. */
export function isoAtHour(date: Date, hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

/** "Date" → all-day date-only key "YYYY-MM-DD" usable as dtstart/dtend. */
export function allDayIso(date: Date): string {
  return dateKey(date);
}

/** Event's start/end decimal hours within a single day. Returns null
 *  if the event doesn't intersect that day in its timed-portion. */
export function eventDayHours(evt: CalendarEvent, day: Date): { start: number; end: number } | null {
  if (evt.allDay) return null;
  const dStart = new Date(evt.dtstart);
  const dEnd = new Date(evt.dtend);
  if (!sameDay(dStart, day) && !sameDay(dEnd, day) && !(dStart < day && dEnd > day)) {
    return null;
  }
  const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day); dayEnd.setHours(24, 0, 0, 0);
  const clampStart = dStart < dayStart ? dayStart : dStart;
  const clampEnd = dEnd > dayEnd ? dayEnd : dEnd;
  const start = clampStart.getHours() + clampStart.getMinutes() / 60;
  let end = clampEnd.getHours() + clampEnd.getMinutes() / 60;
  if (end === 0 && clampEnd.getDate() !== clampStart.getDate()) end = 24;
  if (end <= start) end = start + 0.5; // minimum 30-min display block
  return { start, end };
}

/** Inclusive day-range an all-day event spans, as local Date objects. */
export function allDayRange(evt: CalendarEvent): { start: Date; end: Date } | null {
  if (!evt.allDay) return null;
  const start = parseDateOnly(evt.dtstart);
  // Per RFC 5545, all-day dtend is exclusive — the day after the last
  // included day. Subtract 1 day so our range covers actual occupied days.
  const endExclusive = parseDateOnly(evt.dtend);
  const end = new Date(endExclusive);
  end.setDate(end.getDate() - 1);
  if (end < start) return { start, end: start };
  return { start, end };
}

export function parseDateOnly(s: string): Date {
  if (isDateOnly(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(s);
}

/* ────────────────────────────────────────────────────────────────────
 * layoutOverlaps — interval-graph coloring for concurrent events on a
 * single day. Returns column index + total columns per event so the
 * renderer can compute width = 100% / cols.
 *
 * Ported from the design prototype (chat/design_handoff_hub_ui/sections/
 * calendar.jsx) — input is a list of `{ start, end }` decimal-hour
 * pairs (sorted is fine but we re-sort defensively).
 * ──────────────────────────────────────────────────────────────────── */
export interface PlacedEvent<T extends { start: number; end: number }> {
  evt: T;
  col: number;
  cols: number;
}

export function layoutOverlaps<T extends { start: number; end: number }>(events: T[]): PlacedEvent<T>[] {
  if (events.length === 0) return [];
  const sorted = [...events].sort((a, b) => a.start - b.start || a.end - b.end);
  const columns: number[] = [];
  const placed: PlacedEvent<T>[] = sorted.map((evt) => {
    let col = columns.findIndex((endTime) => endTime <= evt.start);
    if (col === -1) col = columns.length;
    columns[col] = evt.end;
    return { evt, col, cols: 1 };
  });
  // Cluster-width pass — each event's `cols` becomes the max column
  // index + 1 across everything it actually overlaps with. Quadratic,
  // but day-sized N (a few dozen at worst) keeps it cheap.
  placed.forEach((p) => {
    const overlapping = placed.filter(
      (q) => p.evt.start < q.evt.end && q.evt.start < p.evt.end,
    );
    p.cols = Math.max(...overlapping.map((q) => q.col)) + 1;
  });
  return placed;
}

/** Half-hour options for the time picker (0.0 → 23.5). */
export function halfHourOptions(): number[] {
  const out: number[] = [];
  for (let h = 0; h < 24; h++) {
    out.push(h);
    out.push(h + 0.5);
  }
  return out;
}
